import { Check, Layers, Search, Users } from 'lucide-react'
import { useState } from 'react'
import { SegmentedControl } from '@/components/ui/controls'
import { Input } from '@/components/ui/field'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlays'
import { useQuery } from '@tanstack/react-query'
import { listPeople, peopleSync } from '@/data/people'
import { spacesSync } from '@/data/spaces'
import type { MeetingFilters, PeopleRelation, SpaceKind } from '@/data/types'
import {
  DEFAULT_SPACE_KIND,
  SpaceKindTabs,
  filterByKind,
} from '@/features/spaces/SpaceKindTabs'
import { cn } from '@/lib/utils'

/**
 * The two filter dropdowns the meetings list is narrowed with.
 *
 * They live here rather than inside the toolbar because the chat's scope picker
 * offers the SAME two, over the same `MeetingFilters` — a composer that filtered
 * "shared with Rachel" its own way would be a second answer to a question the
 * meetings list already answers, and the two would drift on the first change to
 * either.
 */
export function SpaceFilter({
  selected,
  onToggle,
}: {
  selected: string[]
  onToggle: (id: string) => void
}) {
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<SpaceKind>(DEFAULT_SPACE_KIND)
  const normalized = query.trim().toLowerCase()
  // A query spans BOTH kinds. Searching "Lumen" while Projects is open and
  // being told nothing matches is worse than a result list that crosses the
  // tab: the reader knows the space exists, and the tab is not what they were
  // asking about.
  const visible = (normalized ? spacesSync() : filterByKind(spacesSync(), kind)).filter(
    (space) =>
      !normalized ||
      space.name.toLowerCase().includes(normalized) ||
      space.code.toLowerCase().includes(normalized)
  )

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) return
        setQuery('')
        setKind(DEFAULT_SPACE_KIND)
      }}
    >
      <PopoverTrigger
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-sm)] border px-2.5 text-xs outline-none',
          'border-[var(--color-border-strong)] bg-[var(--color-surface)] transition-colors duration-100',
          selected.length > 0
            ? 'text-[var(--color-fg)]'
            : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
        )}
      >
        <Layers className="size-3.5" />
        Spaces
        {selected.length > 0 ? (
          <span className="tnum text-2xs text-[var(--color-fg-subtle)]">{selected.length}</span>
        ) : null}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-0">
        <div className="relative border-b border-[var(--color-border)]">
          <Search className="absolute top-1/2 left-2.5 size-3 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search spaces…"
            aria-label="Search spaces"
            className="h-8 rounded-none border-0 pl-7 text-xs focus-visible:ring-0"
          />
        </div>

        <div className="border-b border-[var(--color-border)] px-2 py-2">
          <SpaceKindTabs value={kind} onChange={setKind} />
        </div>

        <ul className="max-h-64 overflow-y-auto p-1">
          {visible.map((space) => (
            <li key={space.id}>
              <button
                type="button"
                onClick={() => onToggle(space.id)}
                className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left text-xs hover:bg-[var(--color-surface-hover)]"
              >
                <span className="w-9 shrink-0 font-mono text-2xs text-[var(--color-fg-subtle)]">
                  {space.code}
                </span>
                <span className="min-w-0 flex-1 truncate text-[var(--color-fg)]">{space.name}</span>
                {selected.includes(space.id) ? (
                  <Check className="size-3.5 shrink-0 text-[var(--color-primary)]" />
                ) : null}
              </button>
            </li>
          ))}
          {visible.length === 0 ? (
            <li className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
              No spaces match.
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

const RELATIONS: Array<{ value: PeopleRelation; label: string; hint: string }> = [
  { value: 'participant', label: 'Attended', hint: 'Meetings they were in' },
  { value: 'access', label: 'Can access', hint: 'Meetings they can open at all' },
  { value: 'shared', label: 'Shared with', hint: 'Meetings handed to them explicitly' },
]

/**
 * People, asked three different ways.
 *
 * Attending, being able to open, and having been sent a link are separate
 * facts that only partly overlap — an external client holds a share link to a
 * call they never joined, and a colleague who was in the room may sit outside
 * the org grant. One flat people filter silently picks one of the three and
 * answers the other two wrongly, so the relation is chosen first and the
 * selection is kept per relation.
 */
export function PeopleFilter({
  people,
  onChange,
}: {
  people: MeetingFilters['people']
  onChange: (people: MeetingFilters['people']) => void
}) {
  const [relation, setRelation] = useState<PeopleRelation>('participant')
  const [query, setQuery] = useState('')

  const selected = people[relation]
  const total = people.participant.length + people.access.length + people.shared.length

  const normalized = query.trim().toLowerCase()
  // The tenant's directory, not the filter's own selections — which is what
  // `people` means two lines up. Loaded here rather than passed in so the
  // popover is the only thing that pays for it, and only once opened.
  const directory = useQuery({ queryKey: ['people'], queryFn: listPeople }).data ?? peopleSync()
  const visible = directory.filter(
    (person) =>
      !normalized ||
      person.name.toLowerCase().includes(normalized) ||
      person.email.toLowerCase().includes(normalized)
  )

  const toggle = (email: string) =>
    onChange({
      ...people,
      [relation]: selected.includes(email)
        ? selected.filter((item) => item !== email)
        : [...selected, email],
    })

  return (
    <Popover onOpenChange={(open) => !open && setQuery('')}>
      <PopoverTrigger
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-sm)] border px-2.5 text-xs outline-none',
          'border-[var(--color-border-strong)] bg-[var(--color-surface)] transition-colors duration-100',
          total > 0
            ? 'text-[var(--color-fg)]'
            : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
        )}
      >
        <Users className="size-3.5" />
        People
        {total > 0 ? (
          <span className="tnum text-2xs text-[var(--color-fg-subtle)]">{total}</span>
        ) : null}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-[var(--color-border)] p-2">
          <SegmentedControl
            size="sm"
            className="w-full [&>button]:flex-1"
            value={relation}
            onValueChange={setRelation}
            options={RELATIONS.map(({ value, label }) => ({ value, label }))}
          />
          <p className="mt-1.5 px-0.5 text-2xs text-[var(--color-fg-subtle)]" aria-live="polite">
            {RELATIONS.find((item) => item.value === relation)?.hint}
          </p>
        </div>

        <div className="relative border-b border-[var(--color-border)]">
          <Search className="absolute top-1/2 left-2.5 size-3 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people…"
            aria-label="Search people"
            className="h-8 rounded-none border-0 pl-7 text-xs focus-visible:ring-0"
          />
        </div>

        <ul className="max-h-64 overflow-y-auto p-1">
          {visible.map((person) => {
            // Selections in the other two relations still count, so they are
            // shown rather than hidden — otherwise clearing one is invisible.
            const others = RELATIONS.filter(
              (item) => item.value !== relation && people[item.value].includes(person.email)
            )
            return (
              <li key={person.email}>
                <button
                  type="button"
                  onClick={() => toggle(person.email)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-[var(--color-surface-hover)]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-[var(--color-fg)]">
                      {person.name}
                    </span>
                    <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                      {person.email}
                    </span>
                  </span>
                  {others.length > 0 ? (
                    <span className="shrink-0 text-2xs text-[var(--color-fg-subtle)]">
                      {others.map((item) => item.label.toLowerCase()).join(', ')}
                    </span>
                  ) : null}
                  {selected.includes(person.email) ? (
                    <Check className="size-3.5 shrink-0 text-[var(--color-primary)]" />
                  ) : null}
                </button>
              </li>
            )
          })}
          {visible.length === 0 ? (
            <li className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
              No matches.
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
